import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getEAArtifact, promoteEAArtifact, previewEAArtifact, downloadEAArtifact } from '../services/ea-api'
import { getClient } from '../lib/aperture'
import EAVendorBadge from '../components/ea/EAVendorBadge'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const PHASE_COLORS: Record<string, string> = {
  requirements: '#3b82f6',
  design: '#8b5cf6',
  deployment: '#10b981',
  qc: '#f59e0b',
  deliverables: '#06b6d4',
}

const PHASE_LABELS: Record<string, string> = {
  requirements: 'Requirements',
  design: 'Design',
  deployment: 'Deployment',
  qc: 'QC',
  deliverables: 'Deliverables',
}

const QC_STATUS_COLORS: Record<string, string> = {
  pending_review: '#f59e0b',
  approved: '#10b981',
  approved_with_findings: '#f59e0b',
  rejected: '#ef4444',
}

const QC_STATUS_LABELS: Record<string, string> = {
  pending_review: 'Pending Review',
  approved: 'Approved',
  approved_with_findings: 'Approved w/ Findings',
  rejected: 'Rejected',
}

const FORMAT_ICONS: Record<string, string> = {
  md: '\u{1F4C4}',
  docx: '\u{1F4DD}',
  xlsx: '\u{1F4CA}',
  png: '\u{1F5BC}\uFE0F',
  drawio: '\u{1F5BC}\uFE0F',
  mmd: '\u{1F5BC}\uFE0F',
  cli: '\u2699\uFE0F',
  xml: '\u2699\uFE0F',
  tf: '\u2699\uFE0F',
  yml: '\u2699\uFE0F',
  json: '\u2699\uFE0F',
}

const ARTIFACT_TYPE_LABELS: Record<string, string> = {
  discovery_questionnaire: 'Discovery Questionnaire',
  hld: 'High-Level Design',
  lld: 'Low-Level Design',
  bom: 'Bill of Materials',
  risk_register: 'Risk Register',
  diagram: 'Diagram',
  config: 'Configuration',
  script: 'Script',
  checklist: 'Checklist',
  runbook: 'Runbook',
  terraform: 'Terraform',
  ansible: 'Ansible',
  qc_report: 'QC Report',
  qc_checklist: 'QC Checklist',
  qc_findings: 'QC Findings',
  standards_compliance_check: 'Standards Compliance Check',
  integrated_architecture: 'Integrated Architecture',
  combined_bom: 'Combined BOM',
}

const PREVIEWABLE_FORMATS = ['md', 'json', 'yml', 'xml', 'tf', 'cli']

function formatFileSize(bytes?: number): string {
  if (!bytes) return 'Unknown'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function EAArtifactPreview() {
  const { projectId, artifactId } = useParams<{ projectId: string; artifactId: string }>()
  const queryClient = useQueryClient()
  const [downloading, setDownloading] = useState(false)

  const { data: artifact, isLoading } = useQuery({
    queryKey: ['ea-artifact', artifactId],
    queryFn: () => getEAArtifact(artifactId!).then((res) => res.data),
    enabled: !!artifactId,
  })

  const isPreviewable = artifact && PREVIEWABLE_FORMATS.includes(artifact.file_format)

  const { data: previewData, isLoading: previewLoading } = useQuery({
    queryKey: ['ea-artifact-preview', artifactId],
    queryFn: () => previewEAArtifact(artifactId!).then((res) => res.data),
    enabled: !!artifactId && !!isPreviewable,
  })

  const promoteMutation = useMutation({
    mutationFn: () => promoteEAArtifact(artifactId!),
    onSuccess: () => {
      getClient()?.capture('output_metric', { metric_key: 'ea_artifacts_promoted', value: 1 })
      queryClient.invalidateQueries({ queryKey: ['ea-artifact', artifactId] })
      queryClient.invalidateQueries({ queryKey: ['ea-artifacts', projectId] })
    },
  })

  const handleDownload = async () => {
    if (!artifact) return
    setDownloading(true)
    try {
      const response = await downloadEAArtifact(artifactId!)
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = artifact.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      // Download error - could show a toast in future
    } finally {
      setDownloading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)',
            }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading artifact...</p>
        </div>
      </div>
    )
  }

  if (!artifact) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            Artifact not found
          </h2>
          <Link
            to={`/ea/projects/${projectId}/artifacts`}
            className="text-sm"
            style={{ color: '#60a5fa' }}
          >
            Back to Artifacts
          </Link>
        </div>
      </div>
    )
  }

  const phaseColor = PHASE_COLORS[artifact.phase] || '#94a3b8'
  const qcColor = artifact.qc_status ? QC_STATUS_COLORS[artifact.qc_status] : undefined
  const qcLabel = artifact.qc_status ? QC_STATUS_LABELS[artifact.qc_status] : undefined
  const formatIcon = FORMAT_ICONS[artifact.file_format] || '\u{1F4C4}'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/ea/projects/${projectId}/artifacts`}
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(100, 180, 255, 0.15)',
              color: 'rgba(148, 163, 184, 0.8)',
            }}
            aria-label="Back to artifacts"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </Link>
          <div>
            <div className="flex items-center space-x-3 mb-1">
              <span className="text-lg">{formatIcon}</span>
              <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>
                {artifact.filename}
              </h1>
            </div>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              {ARTIFACT_TYPE_LABELS[artifact.artifact_type] || artifact.artifact_type}
              <span className="mx-1.5">&middot;</span>
              v{artifact.version}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
              border: '1px solid rgba(96, 165, 250, 0.4)',
              color: '#e2e8f0',
            }}
          >
            {downloading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-download"></i>
                <span>Download</span>
              </>
            )}
          </button>
          {!artifact.promoted_to_deliverables && (
            <button
              onClick={() => promoteMutation.mutate()}
              disabled={promoteMutation.isPending}
              className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%)',
                border: '1px solid rgba(6, 182, 212, 0.4)',
                color: '#e2e8f0',
              }}
            >
              {promoteMutation.isPending ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  <span>Promoting...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-star"></i>
                  <span>Promote to Deliverables</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Success/Error messages */}
      {promoteMutation.isSuccess && (
        <div
          className="rounded-xl p-4"
          style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)' }}
        >
          <div className="flex items-center space-x-3">
            <i className="fa-solid fa-check-circle" style={{ color: '#10b981' }}></i>
            <p className="text-sm font-medium" style={{ color: '#34d399' }}>
              Artifact promoted to deliverables successfully
            </p>
          </div>
        </div>
      )}
      {promoteMutation.isError && (
        <div
          className="rounded-xl p-4"
          style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}
        >
          <div className="flex items-center space-x-3">
            <i className="fa-solid fa-exclamation-triangle" style={{ color: '#ef4444' }}></i>
            <p className="text-sm font-medium" style={{ color: '#f87171' }}>
              {(promoteMutation.error as any)?.response?.data?.detail || 'Failed to promote artifact'}
            </p>
          </div>
        </div>
      )}

      {/* Metadata badges row */}
      <div className="flex items-center space-x-3 flex-wrap gap-y-2">
        <span
          className="px-2.5 py-1 text-xs font-medium rounded-full"
          style={{
            background: `${phaseColor}22`,
            color: phaseColor,
            border: `1px solid ${phaseColor}44`,
          }}
        >
          {PHASE_LABELS[artifact.phase] || artifact.phase}
        </span>
        {artifact.vendor && <EAVendorBadge vendor={artifact.vendor} size="md" />}
        <span
          className="px-2.5 py-1 text-xs font-medium rounded-md"
          style={{
            background: 'rgba(100, 116, 139, 0.15)',
            color: 'rgba(148, 163, 184, 0.7)',
            border: '1px solid rgba(100, 116, 139, 0.2)',
          }}
        >
          v{artifact.version}
        </span>
        <span
          className="px-2.5 py-1 text-xs font-medium rounded-md"
          style={{
            background: 'rgba(100, 116, 139, 0.15)',
            color: 'rgba(148, 163, 184, 0.7)',
            border: '1px solid rgba(100, 116, 139, 0.2)',
          }}
        >
          .{artifact.file_format}
        </span>
        {qcColor && qcLabel && (
          <span
            className="px-2.5 py-1 text-xs font-medium rounded-full"
            style={{
              background: `${qcColor}22`,
              color: qcColor,
              border: `1px solid ${qcColor}44`,
            }}
          >
            {qcLabel}
          </span>
        )}
        {artifact.promoted_to_deliverables && (
          <span
            className="px-2.5 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{
              background: 'rgba(6, 182, 212, 0.15)',
              color: '#22d3ee',
              border: '1px solid rgba(6, 182, 212, 0.3)',
            }}
          >
            <i className="fa-solid fa-star text-[10px]"></i>
            <span>Promoted to Deliverables</span>
          </span>
        )}
        {artifact.is_current && (
          <span
            className="px-2.5 py-1 text-xs font-medium rounded-full"
            style={{
              background: 'rgba(16, 185, 129, 0.15)',
              color: '#34d399',
              border: '1px solid rgba(16, 185, 129, 0.3)',
            }}
          >
            Current
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl p-6" style={glassPanel}>
            {isPreviewable ? (
              <div>
                <h2 className="text-lg font-bold mb-4" style={{ color: '#e2e8f0' }}>
                  Preview
                </h2>
                {previewLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <i
                        className="fa-solid fa-spinner fa-spin text-xl mb-3 block"
                        style={{ color: '#60a5fa' }}
                      ></i>
                      <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                        Loading preview...
                      </p>
                    </div>
                  </div>
                ) : previewData?.content ? (
                  <div
                    className="rounded-xl p-6 overflow-auto max-h-[70vh]"
                    style={{
                      background: 'rgba(30, 41, 59, 0.4)',
                      border: '1px solid rgba(100, 180, 255, 0.08)',
                    }}
                  >
                    <pre
                      className="text-sm whitespace-pre-wrap break-words font-mono leading-relaxed"
                      style={{ color: 'rgba(226, 232, 240, 0.9)' }}
                    >
                      {previewData.content}
                    </pre>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                      Preview not available. Use the download button to view this file.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <div
                  className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6"
                  style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                  }}
                >
                  <span className="text-4xl">{formatIcon}</span>
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                  Preview not available for .{artifact.file_format} files
                </h3>
                <p className="text-sm mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  Download the file to view its contents
                </p>
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="px-6 py-3 rounded-xl text-sm font-medium inline-flex items-center space-x-2 transition-all duration-200 hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
                    border: '1px solid rgba(96, 165, 250, 0.4)',
                    color: '#e2e8f0',
                  }}
                >
                  <i className="fa-solid fa-download"></i>
                  <span>{downloading ? 'Downloading...' : 'Download File'}</span>
                </button>
              </div>
            )}
          </div>

          {/* Description */}
          {artifact.description && (
            <div className="rounded-2xl p-6 mt-6" style={glassPanel}>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                Description
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(226, 232, 240, 0.9)' }}>
                {artifact.description}
              </p>
            </div>
          )}
        </div>

        {/* Metadata Sidebar */}
        <div className="space-y-6">
          {/* Artifact Info */}
          <div className="rounded-2xl p-6" style={glassPanel}>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              Artifact Info
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Filename
                </p>
                <p className="text-sm font-medium font-mono" style={{ color: '#e2e8f0' }}>
                  {artifact.filename}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Artifact Type
                </p>
                <p className="text-sm font-medium" style={{ color: '#e2e8f0' }}>
                  {ARTIFACT_TYPE_LABELS[artifact.artifact_type] || artifact.artifact_type}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  File Format
                </p>
                <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  .{artifact.file_format}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  File Size
                </p>
                <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  {formatFileSize(artifact.file_size_bytes)}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Version
                </p>
                <p className="text-sm" style={{ color: '#e2e8f0' }}>
                  v{artifact.version}
                </p>
              </div>

              {artifact.vendor && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Vendor
                  </p>
                  <EAVendorBadge vendor={artifact.vendor} size="md" />
                </div>
              )}
            </div>
          </div>

          {/* Storage & Links */}
          <div className="rounded-2xl p-6" style={glassPanel}>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              Storage & Links
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Blob Path
                </p>
                <p
                  className="text-xs font-mono break-all"
                  style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                >
                  {artifact.blob_path}
                </p>
              </div>

              {artifact.pipeline_task_id && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Source Task
                  </p>
                  <Link
                    to={`/ea/projects/${projectId}/pipeline`}
                    className="text-sm transition-colors hover:underline"
                    style={{ color: '#60a5fa' }}
                  >
                    View Pipeline Task
                    <i className="fa-solid fa-external-link ml-1.5 text-xs"></i>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Promotion Info */}
          <div className="rounded-2xl p-6" style={glassPanel}>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              Promotion
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Status
                </p>
                {artifact.promoted_to_deliverables ? (
                  <span
                    className="px-2.5 py-1 text-xs font-medium rounded-full inline-flex items-center space-x-1"
                    style={{
                      background: 'rgba(6, 182, 212, 0.15)',
                      color: '#22d3ee',
                      border: '1px solid rgba(6, 182, 212, 0.3)',
                    }}
                  >
                    <i className="fa-solid fa-star text-[10px]"></i>
                    <span>Promoted</span>
                  </span>
                ) : (
                  <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Not promoted
                  </span>
                )}
              </div>

              {artifact.promoted_at && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Promoted At
                  </p>
                  <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                    {new Date(artifact.promoted_at).toLocaleString()}
                  </p>
                </div>
              )}

              {artifact.promoted_by && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Promoted By
                  </p>
                  <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                    {artifact.promoted_by}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="rounded-2xl p-6" style={glassPanel}>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              Dates
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Created
                </p>
                <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  {new Date(artifact.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Last Updated
                </p>
                <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  {new Date(artifact.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
